export type GDriveFileInfo = {
  id: string
  name: string
  mimeType: string
}

export interface GDriveFileWithPath extends GDriveFile {
  namePath: string
}

export type GDriveFileList = {
  kind: 'drive#fileList'
  files: GDriveFile[]
}

export type GDriveFile = {
  driveId?: string
  id: string
  kind: 'drive#file'
  mimeType: string
  name: string
  files?: GDriveFile | GDriveFileList
}

export type GDriveMatchingTableRow = {
  name: string
  grade: number | string
  unit: number
  lesson: number
  file: GDriveFile
}

export type GDriveMatchingTableProps = {
  rows: GDriveMatchingTableRow[]
}

export enum MimeTypes {
  csv = 'text/csv',
  html = 'text/html',
}

export type GDriveSpreadsheetT = {
  properties: { title: string }
  spreadsheetId: string
  spreadsheetUrl: string
  sheets: GDriveSheetT[]
  error?: {
    code: number
    message: string
    status: string
  }
}

export type GDriveSheetT = {
  properties: GDriveSheetProperties
  data: GDriveSheetData[]
}

export type GDriveSheetData = {
  rowData: GDriveRowData[]
}

export type GDriveRowData = {
  values: GDriveValue[]
}

export type GDriveValue = {
  formattedValue?: string
  hyperlink?: string
  userEnteredValue?: { stringValue: string }
  effectiveValue?: { stringValue: string }
}

type SheetTypes = 'GRID' | 'OBJECT' | 'DATA_SOURCE'

export type GDriveSheetProperties = {
  index: number
  sheetId: number
  sheetType: SheetTypes
  title: string
}

export class GDriveSheet {
  properties: GDriveSheetProperties
  data: GDriveSheetData

  constructor(data: GDriveSheetT) {
    this.properties = data.properties
    this.data = data.data[0] // We will only have one data object since we're getting just one range of data
  }

  getRows() {
    return this.data.rowData.map((row) => row.values)
  }

  getRow(rowNum: number): GDriveValue[] {
    return this.data.rowData[rowNum].values
  }

  getRowValues(rowNum: number): (string | undefined)[] {
    return this.data.rowData[rowNum].values.map((value) => value.formattedValue)
  }

  getCell(cell: { row: number; col: number }): GDriveValue {
    return this.data.rowData[cell.row].values[cell.col]
  }

  getIndicesOfStringsInRow(
    rowNumber: number,
    strings: string[],
  ): Record<string, number> {
    const row = this.getRow(rowNumber)
    const indices: Record<string, number> = {}
    strings.forEach((string) => {
      indices[string] = row.findIndex(
        (cell) =>
          cell.formattedValue?.trim().toUpperCase() ===
          string.trim().toUpperCase(),
      )
    })
    return indices
  }
}

export class GDriveSpreadsheet {
  spreadsheetId: string
  spreadsheet: GDriveSpreadsheetT
  spreadsheetUrl: string
  sheets: GDriveSheet[]

  constructor(sheetId: string, spreadsheet: GDriveSpreadsheetT) {
    this.spreadsheetId = sheetId
    this.spreadsheet = spreadsheet
    this.spreadsheetUrl = spreadsheet.spreadsheetUrl
    this.sheets = spreadsheet.sheets.map((sheet) => new GDriveSheet(sheet))
  }
}

export enum WrapStrategy {
  OVERFLOW_CELL = 'OVERFLOW_CELL',
  CLIP = 'CLIP',
  WRAP = 'WRAP',
}

export type SheetTab = { sheetId: number; title: string }

export type SheetTabInput = {
  title: string
  data?: string[][]
  frozenRowCount?: number
  sheetId?: number // Explicit sheet ID for batchUpdate operations
}

export type SpreadsheetCreateRequest = {
  properties: { title: string }
  sheets?: Array<{
    properties?: {
      sheetId?: number
      title: string
      gridProperties?: {
        frozenRowCount?: number
        frozenColumnCount?: number
      }
    }
    data?: GDriveSheetData[]
  }>
}

// Types for Google Sheets batchUpdate API
export type SheetRange = {
  sheetId: number
  startRowIndex?: number
  endRowIndex?: number
  startColumnIndex?: number
  endColumnIndex?: number
}

export type SheetColor = {
  red: number
  green: number
  blue: number
}

export type SheetCellFormat = {
  backgroundColor?: SheetColor
  textFormat?: {
    bold?: boolean
    foregroundColor?: SheetColor
  }
}

export type SheetDataValidationRule = {
  condition: {
    type: string
    values: { userEnteredValue: string }[]
  }
  showCustomUi: boolean
  strict: boolean
}

export type BatchUpdateRequest = {
  repeatCell?: {
    range: SheetRange
    cell: {
      userEnteredFormat?: SheetCellFormat
    }
    fields: string
  }
  setDataValidation?: {
    range: Required<SheetRange>
    rule: SheetDataValidationRule
  }
  updateDimensionProperties?: {
    range: {
      sheetId: number
      dimension: string
      startIndex: number
      endIndex: number
    }
    properties: {
      hiddenByUser?: boolean
    }
    fields: string
  }
}
